import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { loadCompanyDataAsync } from "store/actions/authActions";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
//#endregion
//#region types
import { SettingsFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { InvoiceConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { CompanySettingsFormContainerProps } from "models/propTypes/settingsPropTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
//#endregion
//#region components
import CompanySettingsForm from "./companySettingsForm";
import { Skeletons, useMountedState } from "components/common";
import { useSettingsService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { LocalizationHelperInstance } from "utils/helpers";

const CompanySettingsFormContainer: ForwardRefRenderFunction<FormHandles, CompanySettingsFormContainerProps> = (
  props,
  ref
) => {
  const settingsService = useSettingsService();
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const [loading, setLoading] = React.useState<boolean>(true);
  const { getMounted } = useMountedState();

  const validationSchema = Yup.object<SettingsFormTypes.CompanySettings>({
    name: Yup.string(),
    orgNumber: Yup.string(),
    mail: Yup.string().email(TranslationKeys.Validations.InvalidEmail),
    phone: Yup.string().validatePhone(TranslationKeys.Validations.InvalidPhone),
    visitorStreet1: Yup.string().required(TranslationKeys.Validations.Required),
    visitorStreet2: Yup.string(),
    visitorZipCode: Yup.string().required(TranslationKeys.Validations.Required),
    visitorCity: Yup.string().required(TranslationKeys.Validations.Required),
    billingStreet1: Yup.string(),
    billingStreet2: Yup.string(),
    billingZipCode: Yup.string(),
    billingCity: Yup.string(),
    visitingCountryCode: Yup.string(),
    visitingCountryName: Yup.string(),
    billingCountryCode: Yup.string(),
    billingCountryName: Yup.string(),
    companyImage: Yup.array()
      .max(1, TranslationKeys.Settings.OneCompanyImage)
      .of(
        Yup.object<UploadedFile>({
          data: Yup.string(),
          contentType: Yup.string(),
          fileName: Yup.string(),
          fileSize: Yup.number(),
        })
      ),
  });

  const [defaultValues, setDefaultValues] = React.useState<SettingsFormTypes.CompanySettings>();

  const handleSubmit = (values: SettingsFormTypes.CompanySettings) => {
    const companyLogo = values.companyImage && values.companyImage[0];
    const settingsPost = {
      phone: values.phone,
      mail: values.mail,
      visitorStreet1: values.visitorStreet1,
      visitorStreet2: values.visitorStreet2,
      visitorCountryCode: values.visitingCountryCode,
      visitorZipCode: values.visitorZipCode,
      visitorCity: values.visitorCity,
      companyLogo:
        companyLogo &&
        ({ imageData: companyLogo.data, contentType: companyLogo.contentType } as WintSuperkollAPIModel.ProfileImage),
    } as WintSuperkollAPIModel.CompanySettingsPost;

    return settingsService.updateCompanySettings(settingsPost).then((res) => {
      defaultValues && dispatch(loadCompanyDataAsync());
      if (getMounted()) {
        setDefaultValues(values);
      }
    });
  };

  React.useEffect(() => {
    settingsService.getCompanySettings().then((companySettings) => {
      if (getMounted()) {
        setDefaultValues({
          name: companySettings?.name ?? "",
          orgNumber: companySettings?.org ?? "",
          phone: companySettings?.phone,
          mail: companySettings?.mail,
          visitorStreet1: companySettings?.visitorStreet1,
          visitorStreet2: companySettings?.visitorStreet2,
          visitorZipCode: companySettings?.visitorZipCode,
          visitorCity: companySettings?.visitorCity,
          billingStreet1: companySettings?.billingStreet1,
          billingStreet2: companySettings?.billingStreet2,
          billingZipCode: companySettings?.billingZipCode,
          billingCity: companySettings?.billingCity,
          visitingCountryCode: companySettings?.visitorCountryCode,
          visitingCountryName: LocalizationHelperInstance.getFormattedMessage(
            companySettings && companySettings.visitorCountryCode
              ? companySettings.visitorCountryCode
              : InvoiceConstants.DefaultInvoiceCountry
          ),
          billingCountryCode: companySettings?.billingCountryCode,
          billingCountryName: LocalizationHelperInstance.getFormattedMessage(
            companySettings && companySettings.billingCountryCode
              ? companySettings.billingCountryCode
              : InvoiceConstants.DefaultInvoiceCountry
          ),
          companyImage: companySettings?.companyLogo
            ? [
                {
                  data: companySettings?.companyLogo?.imageData,
                  contentType: companySettings?.companyLogo?.contentType,
                } as UploadedFile,
              ]
            : [],
        });
        setLoading(false);
      }
    });
  }, []);

  return loading ? (
    <Skeletons.FormSkeleton />
  ) : (
    <CompanySettingsForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      handleSubmit={handleSubmit}
      defaultValues={defaultValues}
    />
  );
};

export default forwardRef(CompanySettingsFormContainer);
